import { useState, useEffect, useMemo } from 'react';
import roleService, { ROLES } from '../service/RoleService';

/**
 * Custom hook for role-based authorization
 * @returns {Object} Role checking utilities
 */
export const useRole = () => {
    const [user, setUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [effectiveRoles, setEffectiveRoles] = useState([]);

    useEffect(() => {
        // Initialize user and roles
        const currentUser = roleService.getCurrentUser();
        setUser(currentUser);
        setRoles(roleService.getUserRoles());
        setEffectiveRoles(roleService.getEffectiveRoles());
    }, []);

    // Memoize role checks to prevent repeated calculations
    const roleChecks = useMemo(() => ({
        // Authentication check
        isAuthenticated: roleService.isAuthenticated(),

        // Specific role checks
        isStaff: roleService.isStaff(),
        isTeamLeader: roleService.isTeamLeader(),
        isMarcom: roleService.isMarcom(),
        isFinance: roleService.isFinance(),
        isHR: roleService.isHR(),
        isManagement: roleService.isManagement(),
        isManager: roleService.isManager(),
        isOwner: roleService.isOwner(),
        hasAllOfficeAccess: roleService.hasAllOfficeAccess(),

        // Permission checks
        canAccessFinancialData: roleService.canAccessFinancialData(),
        canManageHR: roleService.canManageHR(),
        canManageAppraisals: roleService.canManageAppraisals(),
        canManageTeams: roleService.canManageTeams(),
        isAdmin: roleService.isAdmin(),
        canUnarchiveProject: roleService.canUnarchiveProject(),
    }), []);

    return {
        // User data
        user,
        roles,
        effectiveRoles,
        
        // Spread all memoized role checks
        ...roleChecks,
        
        // Role checking methods (these need parameters so can't be memoized)
        hasRole: (role) => roleService.hasRole(role),
        hasDirectRole: (role) => roleService.hasDirectRole(role),
        hasAnyRole: (roles) => roleService.hasAnyRole(roles),
        hasAnyDirectRole: (roles) => roleService.hasAnyDirectRole(roles),
        hasAllRoles: (roles) => roleService.hasAllRoles(roles),
        hasSpecialAccess: (permissionKey) => roleService.hasSpecialAccess(permissionKey),
        
        // Utility methods
        getUserRoles: () => roleService.getUserRoles(),
        getHighestRole: () => roleService.getHighestRole(),
        getRoleDisplayName: (role) => roleService.getRoleDisplayName(role),
        getRoleColor: (role) => roleService.getRoleColor(role),
        
        // Constants
        ROLES
    };
};

export default useRole;
